/* Harber insights: our own funnel, front-end errors and real-visitor speed, sent to the harberapp backend through the
 * app proxy (/apps/harber/insights/collect). Read in the Harber admin → Insights (plus a weekly report).
 * - Funnel steps (page, product, option, add to bag, bag, checkout) only for visitors who accepted statistics
 *   cookies (Cookiebot); the session id lives in sessionStorage only.
 * - Errors (JS errors, failed bag requests, broken theme images) and speed (LCP, INP, CLS) from everyone, with no
 *   personal data: page type, device, OS, browser (+ major version), language.
 * - Audience (admin Insights → Audience): one anonymous `pv` per page view from every visitor except bots, flagged
 *   v = 1 on a visit's first page (referrer not our own site, not a reload / back-forward); the session id is created
 *   (sessionStorage) and sent only once statistics cookies are accepted, so nothing is stored for the others.
 * - Heatmap (admin Insights → Heatmap): each click (`hc`: the element as a CSS path from the nearest id, where inside
 *   it, whether it did anything, rage clicks) and how far down the page view got (`hs`), only with statistics consent;
 *   the backend keeps them added up per day and page, never per visitor. No text typed in a field is ever read.
 * Events go out in small batches with sendBeacon when the page is hidden, and every 15 s while it stays open. */
/* A page the browser only prerenders (hover on a product link, theme.liquid speculation rules) hasn't been seen:
 * nothing runs until it is actually shown. */
((run) => (document.prerendering ? document.addEventListener('prerenderingchange', run, { once: true }) : run()))(() => {
  if (window.__harberInsights || !navigator.sendBeacon) return;
  window.__harberInsights = true;
  const URL_ = '/apps/harber/insights/collect';
  const T = window.Theme || {};
  const page = (document.body && document.body.dataset.template) || (document.querySelector('meta[name="harber:template"]') || {}).content || '';
  const ua = navigator.userAgent;
  // Same rules as the backend's lib/user-agent.ts; in-app browsers (social apps' web views) first
  const IN_APP = [[/Instagram/, 'Instagram'], [/FBAN|FBAV|FB_IAB|FBIOS|\[FB/, 'Facebook'], [/musical_ly|BytedanceWebview|TikTok|trill_/, 'TikTok'], [/Pinterest/, 'Pinterest'], [/Snapchat/, 'Snapchat'], [/LinkedInApp/, 'LinkedIn'], [/GSA\//, 'Google app']];
  const BROWSERS = [[/Edg(e|A|iOS)?\//, 'Edge', /Edg(?:e|A|iOS)?\/(\d+)/], [/OPR\/|OPT\/|Opera/, 'Opera', /(?:OPR|OPT|Version)\/(\d+)/], [/SamsungBrowser/, 'Samsung', /SamsungBrowser\/(\d+)/], [/Firefox\/|FxiOS/, 'Firefox', /(?:Firefox|FxiOS)\/(\d+)/],
    [/; wv\)/, 'In-app (other)'], [/CriOS|Chrome\//, 'Chrome', /(?:CriOS|Chrome)\/(\d+)/], [/Safari\/.*|Version\//, 'Safari', /Version\/(\d+)/]];
  const iOS = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1); // iPadOS shows a Mac user agent
  const os = iOS ? 'iOS' : /Android/.test(ua) ? 'Android' : /CrOS/.test(ua) ? 'ChromeOS' : /Windows/.test(ua) ? 'Windows' : /Macintosh|Mac OS X/.test(ua) ? 'macOS' : /Linux|X11/.test(ua) ? 'Linux' : 'Other';
  let browser = 'Other', bv = '';
  const app = IN_APP.find(([re]) => re.test(ua));
  if (app) browser = app[1];
  else {
    const hit = BROWSERS.find(([re], i) => re.test(ua) && (i < 6 || (/Safari\//.test(ua) && /Version\//.test(ua))));
    if (hit) { browser = hit[1]; bv = hit[2] ? ((hit[2].exec(ua) || [])[1] || '').slice(0, 4) : ''; }
    else if (iOS && /AppleWebKit/.test(ua)) browser = 'In-app (other)'; // iOS web view (no "Safari" token)
  }
  const device = matchMedia('(pointer: coarse)').matches ? (Math.min(screen.width, screen.height) >= 700 ? 't' : 'm') : 'd';
  // Headless browsers, automation, audits and crawlers are not visitors (Cubot phones are)
  const bot = Boolean(navigator.webdriver) || /bot\b|bot\/|crawl|spider|slurp|HeadlessChrome|Lighthouse|PageSpeed|GTmetrix|Pingdom|PTST|Google-InspectionTool|AdsBot|Mediapartners|facebookexternalhit|Bytespider|phantomjs|selenium|puppeteer|playwright|preview/i.test(ua.replace(/cubot/gi, ''));
  const consent = () => {
    const c = window.Cookiebot && window.Cookiebot.consent;
    return c ? Boolean(c.statistics) : !window.Cookiebot; // no CMP loaded (e.g. outside the banner's regions): allowed
  };
  // Session id: only for visitors who accepted statistics cookies, created when the first batch goes out (so a visitor
  // who accepts on the first page has that page in the session too)
  let sid = '';
  try { sid = sessionStorage.getItem('harber:ins') || ''; } catch { sid = ''; }
  const session = () => {
    if (sid || !consent()) return sid;
    try { sid = Math.random().toString(36).slice(2, 12) + Date.now().toString(36); sessionStorage.setItem('harber:ins', sid); } catch { sid = ''; }
    return sid;
  };
  const ctx = { t: page, p: location.pathname, d: device, b: browser, o: os, bv, l: T.locale || document.documentElement.lang, c: T.country };
  const product = (T.push && T.push.product && T.push.product.handle) || (/\/products\/([^/?#]+)/.exec(location.pathname) || [])[1] || null;
  let queue = [];
  const sent = new Set();
  const add = (e, extra = {}, once = false) => {
    if (once) { if (sent.has(e)) return; sent.add(e); }
    queue.push({ e, ...extra });
    if (queue.length >= 30) flush();
  };
  let landed = false;
  const flush = () => {
    const ok = consent();
    if (ok && !landed) { landed = true; land(); } // where the visit came from: only with consent, once per session
    if (!queue.length) return;
    const body = JSON.stringify({ s: (ok && session()) || null, consent: ok, ctx, ev: queue.splice(0, 40) });
    try { navigator.sendBeacon(URL_, new Blob([body], { type: 'text/plain' })); } catch { /* ignore */ }
  };
  addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') { finalize(); flush(); } });
  addEventListener('pagehide', () => { finalize(); flush(); });
  setInterval(flush, 15000);

  // ---- Audience: one page view per page from everyone (no id without consent); v = 1 when it starts a visit
  if (!bot && !(window.Shopify && window.Shopify.designMode)) {
    let entry = 1;
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      const own = (h) => h.replace(/^www\./, '') === location.hostname.replace(/^www\./, '') || /(^|\.)harberlondon\.com$|\.myshopify\.com$/.test(h);
      const ref = document.referrer ? new URL(document.referrer).hostname : '';
      if ((nav && (nav.type === 'reload' || nav.type === 'back_forward')) || (ref && own(ref))) entry = 0;
    } catch { /* counted as an entry */ }
    // Live visitors (admin Sales / Summary): a random id for this page view, in memory only (never stored): `hb` at most
    // every 45 s while the person is doing something on the page, `bye` when it is left. Like Shopify's live view
    // ("active in the last 5 minutes"), an open tab nobody touches stops counting 5 min after its last activity
    // (25 Oct 2026: beating from every open tab counted ~6× Shopify's visitors on desktop, where tabs stay open for hours)
    const pid = (Math.random().toString(36).slice(2) + Date.now().toString(36)).replace(/[^a-z0-9]/g, '').slice(0, 16);
    // Counted only once a person shows up — a mouse move, a touch, the wheel or a key (Oct 2026): automated browsers
    // that run the theme with an ordinary user agent never do, and doubled the desktop visits next to Shopify's
    // sessions (which filter them by IP). Not `scroll`: restoring the scroll position on a reload fires it too.
    const HUMAN = ['pointermove', 'pointerdown', 'touchstart', 'wheel', 'keydown'];
    let human = false;
    let active = 0;
    let beaten = 0;
    const beat = () => { if (human && active > beaten) { beaten = Date.now(); add('hb', { id: pid, a: 1 }); flush(); } };
    const touch = () => { active = Date.now(); };
    const seen = () => {
      touch();
      if (human) return;
      human = true;
      add('pv', { v: entry, id: pid }, true);
      beat();
      setInterval(beat, 45000);
      // From now on any activity (scroll included) keeps the page live
      addEventListener('scroll', touch, { passive: true });
    };
    HUMAN.forEach((t) => addEventListener(t, seen, { capture: true, passive: true }));
    addEventListener('pagehide', () => { if (human) { add('bye', { id: pid }); flush(); } });
  }

  // ---- Funnel
  add('page', {}, true);
  if (product && page.indexOf('product') === 0) add('product', { h: product }, true);
  document.addEventListener('change', (e) => {
    if (e.target.closest && e.target.closest('[data-option-select], .pdp-option, variant-picker')) add('option', { h: product }, true);
  }, true);
  document.addEventListener('click', (e) => {
    const t = e.target.closest ? e.target : null;
    if (!t) return;
    if (t.closest('[data-atc], .pdp-sticky [type="submit"], [data-quick-add]')) add('atc_click', { h: product });
    // The page is about to leave for checkout: send at once
    else if (t.closest('[name="checkout"], a[href*="/checkout"], [data-checkout]')) { add('checkout', {}, true); flush(); }
    else if (t.closest('shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .shopify-payment-button, [data-shopify-buttoncontainer]')) { add('express', {}, true); flush(); }
    else if (t.closest('.facets, [data-filter], [data-facets]')) add('filter', {}, true);
  }, true);
  document.addEventListener('cart:updated', (e) => { if (e.detail && e.detail.action === 'add') add('atc_ok', { h: product }); });
  document.addEventListener('atc:blocked', (e) => add('atc_blocked', { h: product, x: (e.detail && e.detail.option) || '' }));
  document.addEventListener('cart:open', () => add('cart_open', {}, true));
  document.addEventListener('cart:error', (e) => {
    const d = e.detail || {};
    add('cart_error', { x: `${d.status || ''} ${d.message || ''}`.trim().slice(0, 300), src: String(d.endpoint || '').replace(/^https?:\/\/[^/]+/, '') });
  });
  // Search: the query and how many results it found (zero-result searches show what people can't find)
  if (page === 'search') {
    const s = document.querySelector('[data-search-terms]');
    const q = s ? String(s.getAttribute('data-search-terms') || '').trim().toLowerCase().replace(/\s+/g, ' ').slice(0, 80) : '';
    add('search', q ? { x: q, v: Number(s.getAttribute('data-search-count')) || 0 } : {}, true);
  }
  // Compare page: which two bags were compared (each pair once per page view); with statistics consent like the funnel
  const compared = new Set();
  document.addEventListener('harber:compare', (e) => {
    const a = String(e.detail?.a || '').slice(0, 120), b = String(e.detail?.b || '').slice(0, 120);
    const pair = `${a}|${b}`;
    if (a && b && !compared.has(pair) && compared.size < 20) { compared.add(pair); add('compare', { x: pair }); }
  });
  // Device finder: model numbers we have no sleeve for (Apple model numbers only, nothing personal)
  const missed = new Set();
  document.addEventListener('finder:miss', (e) => {
    const m = String((e.detail && e.detail.model) || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
    if (m.length >= 4 && !missed.has(m)) { missed.add(m); add('finder_miss', { x: m }); }
  });
  // Where the visit came from, once per session: utm / ad click ids, else the referring site, else direct
  // (funnel data: added at the first flush with statistics consent, which can come after this first page)
  function land() { try {
    if (!sessionStorage.getItem('harber:ins-land')) {
      sessionStorage.setItem('harber:ins-land', '1');
      const q = new URLSearchParams(location.search);
      const clean = (v) => String(v || '').toLowerCase().replace(/[|]/g, ' ').trim().slice(0, 60);
      let src = clean(q.get('utm_source')), med = clean(q.get('utm_medium'));
      const camp = clean(q.get('utm_campaign'));
      if (!src) {
        if (q.get('gclid') || q.get('gbraid') || q.get('wbraid') || q.get('gad_source')) { src = 'google'; med = med || 'cpc'; }
        else if (q.get('msclkid')) { src = 'bing'; med = med || 'cpc'; }
        else if (q.get('ttclid')) { src = 'tiktok'; med = med || 'paid'; }
        else if (q.get('fbclid')) { src = 'facebook'; med = med || 'social'; }
      }
      if (!src) {
        let host = '';
        try { host = document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, '') : ''; } catch { host = ''; }
        if (host && host !== location.hostname.replace(/^www\./, '') && !/(^|\.)harberlondon\.com$|myshopify\.com$|shopify\.com$/.test(host)) { src = host; med = 'referral'; }
      }
      queue.push({ e: 'land', x: `${src || 'direct'}|${med || (src ? '' : 'none')}|${camp}`.slice(0, 190) });
    }
  } catch { /* storage blocked */ } }
  addEventListener('CookiebotOnAccept', () => { if (consent()) flush(); }, { once: true });

  // ---- Errors (at most 8 per page, each message once)
  let errCount = 0;
  const seen = new Set();
  // Ours = the theme's own asset files; inline/page-level, Shopify (shopifycloud, wpm) and app scripts are not
  const isOwn = (src) => /\/cdn\/shop\/t\/\d+\/assets\//.test(src || '');
  const err = (kind, msg, src) => {
    msg = String(msg || '').slice(0, 300);
    if (!msg || errCount >= 8 || seen.has(msg)) return;
    seen.add(msg); errCount++;
    add(kind, { x: msg, src: src || '', own: isOwn(src) });
  };
  addEventListener('error', (e) => {
    const t = e.target;
    if (t && t.tagName === 'IMG') {
      const src = t.currentSrc || t.src || '';
      // Interrupted loads (leaving the page, slow network) also fire "error": report only files the server really lacks
      if (/\/cdn\/shop\//.test(src)) {
        fetch(src, { method: 'HEAD' }).then((r) => {
          if (r.status >= 400) err('img_error', `${r.status} ${src.replace(/^https?:/, '').replace(/[?&](width|height|v|crop)=[^&]*/g, '').slice(0, 200)}`, location.pathname);
        }).catch(() => {});
      }
      return;
    }
    if (e.message) err('js_error', e.message, `${e.filename || ''}:${e.lineno || 0}:${e.colno || 0}`);
  }, true);
  // Web push could not move a browser over (assets/web-push.js): the browser's own error, to see why
  document.addEventListener('push:error', (e) => { const d = e.detail || {}; err('js_error', `web push ${d.step || ''}: ${d.message || ''}`.slice(0, 300), 'web-push.js'); });
  addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    const stack = (r && r.stack) || '';
    const m = /(https?:\/\/[^\s)]+):(\d+):(\d+)/.exec(stack);
    err('promise', (r && (r.message || String(r))) || 'unhandled rejection', m ? `${m[1]}:${m[2]}:${m[3]}` : '');
  });

  // ---- Heatmap: clicks and scroll reach (only sent with statistics consent; at most 60 clicks a page)
  const heatOn = !bot && !(window.Shopify && window.Shopify.designMode) && window.CSS && CSS.escape;
  let heatN = 0;
  const recent = [];
  // A path from the nearest element with an id (sections have stable ones: shopify-section-template--…__main), with
  // :nth-of-type where a tag repeats, so the admin can find the same element on the page and draw the click on it
  const pathOf = (el) => {
    const parts = [];
    for (let n = el, i = 0; n && n.nodeType === 1 && i < 14; n = n.parentElement, i++) {
      if (n.id && n.id.length <= 80 && /^[A-Za-z][\w-]*$/.test(n.id)) { parts.unshift(`#${n.id}`); return parts.join(' > '); }
      const tag = n.tagName.toLowerCase();
      if (tag === 'html') break;
      if (!/^[a-z][a-z0-9-]*$/.test(tag)) return null;
      const p = n.parentElement;
      const same = p ? Array.prototype.filter.call(p.children, (c) => c.tagName === n.tagName) : [];
      parts.unshift(same.length > 1 ? `${tag}:nth-of-type(${same.indexOf(n) + 1})` : tag);
      if (tag === 'body') return parts.join(' > ');
    }
    return null;
  };
  // The last clicks of the visit (consented only, sessionStorage): an add to the bag credits them (`ha`), so the
  // heatmap can say which elements lead to a sale and not only which get pressed
  const TRAIL = 'harber:hm-trail';
  const trail = (path) => {
    try {
      const t = JSON.parse(sessionStorage.getItem(TRAIL) || '[]').filter((x) => !(x.pg === location.pathname && x.h === path));
      t.push({ pg: location.pathname, h: path });
      sessionStorage.setItem(TRAIL, JSON.stringify(t.slice(-8)));
    } catch { /* */ }
  };
  document.addEventListener('cart:updated', (e) => {
    if (!heatOn || !consent() || !e.detail || e.detail.action !== 'add') return;
    try { JSON.parse(sessionStorage.getItem(TRAIL) || '[]').slice(-6).forEach((x) => add('ha', { pg: x.pg, h: x.h })); } catch { /* */ }
  });
  document.addEventListener('click', (e) => {
    if (!heatOn || heatN >= 60 || !e.isTrusted || !(e.target instanceof Element) || !consent()) return;
    // Inside a shadow root (the chat window) the document sees only its host: what was pressed comes from the event's
    // path, the place on the page from the host
    const PRESS = 'a[href], button, input, select, textarea, label, summary, [role="button"], [role="link"], [role="tab"], [tabindex]:not([tabindex="-1"])';
    const inner = e.target.shadowRoot && e.composedPath ? e.composedPath().find((n) => n instanceof Element) : null;
    const innerAct = inner && inner !== e.target ? e.composedPath().find((n) => n instanceof Element && n !== e.target && n.matches(PRESS)) : null;
    const raw = e.target.closest('svg') ? e.target.closest('svg').parentElement || e.target : e.target;
    const act = innerAct || raw.closest(PRESS);
    const el = innerAct ? e.target : act || raw;
    const path = pathOf(el);
    if (!path) return;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const tag = el.tagName;
    const kind = /^(INPUT|SELECT|TEXTAREA)$/.test(tag) ? 'field' : tag === 'A' || el.getAttribute('role') === 'link' ? 'link' : act ? 'button' : /^(IMG|PICTURE|VIDEO)$/.test(tag) || el.querySelector(':scope > img, :scope > picture') ? 'media' : 'text';
    // Did nothing: not a link, button or field, and nothing that looks pressable (pointer cursor) under the finger
    // (a tap on a window's backdrop closes it: not dead)
    let dead = !act && raw.tagName !== 'DIALOG';
    try { if (dead && getComputedStyle(inner || raw).cursor === 'pointer') dead = false; } catch { /* */ }
    // Rage: the third click within a second on (almost) the same spot
    const now = performance.now();
    recent.push({ t: now, x: e.clientX, y: e.clientY });
    while (recent.length && now - recent[0].t > 1000) recent.shift();
    const rage = recent.length === 3 && recent.every((c) => Math.abs(c.x - e.clientX) < 30 && Math.abs(c.y - e.clientY) < 30);
    // A label to recognise it in a list: never what was typed in a field
    // (a card's text is its first line — "Wallets", not "WalletsClever Wallet Architecture…")
    const named = innerAct || el;
    const firstLine = (n) => ((n.innerText || n.textContent || '').split('\n').map((t) => t.trim()).find(Boolean) || '');
    const label = kind === 'field' ? (named.getAttribute('aria-label') || named.getAttribute('name') || named.getAttribute('type') || tag.toLowerCase())
      : (named.getAttribute('aria-label') || firstLine(named) || (named.querySelector('img') || named).getAttribute('alt') || '');
    const docH = Math.max(document.documentElement.scrollHeight, 1);
    heatN++;
    trail(path);
    add('hc', {
      h: path, x: String(label).replace(/\s+/g, ' ').trim().slice(0, 60), k: kind,
      gx: Math.min(9, Math.max(0, Math.floor(((e.clientX - r.left) / r.width) * 10))),
      gy: Math.min(9, Math.max(0, Math.floor(((e.clientY - r.top) / r.height) * 10))),
      fx: Math.round((e.clientX / Math.max(innerWidth, 1)) * 1000),
      fy: Math.round(((e.clientY + scrollY) / docH) * 1000),
      ...(dead ? { dead: true } : {}), ...(rage ? { rage: true } : {}),
    });
  }, true);

  // ---- Speed (LCP, INP, CLS) and scroll depth
  const vitals = {};
  let lcpEntry = null;
  // A page opened in a background tab paints only when it is looked at: its LCP measures the visitor, not the page.
  // As in Google's web-vitals, LCP counts only while the page hasn't been hidden yet.
  let firstHidden = document.visibilityState === 'hidden' ? 0 : Infinity;
  addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') firstHidden = Math.min(firstHidden, performance.now()); }, { capture: true });
  try {
    new PerformanceObserver((l) => { for (const e of l.getEntries()) if (e.startTime < firstHidden) { vitals.LCP = e.startTime; lcpEntry = e; } }).observe({ type: 'largest-contentful-paint', buffered: true });
    let cls = 0;
    new PerformanceObserver((l) => { for (const x of l.getEntries()) if (!x.hadRecentInput) cls += x.value; vitals.CLS = cls; }).observe({ type: 'layout-shift', buffered: true });
    new PerformanceObserver((l) => { for (const x of l.getEntries()) if (x.interactionId) vitals.INP = Math.max(vitals.INP || 0, x.duration); }).observe({ type: 'event', buffered: true, durationThreshold: 40 });
  } catch { /* older browsers */ }
  let depth = 0;
  let reach = 0; // lowest point seen, in thousandths of the page's height (heatmap scroll map)
  const onScroll = () => {
    const full = document.documentElement.scrollHeight;
    const h = full - innerHeight;
    if (h > 0) depth = Math.max(depth, Math.min(100, Math.round((scrollY / h) * 100)));
    if (full > 0) reach = Math.max(reach, Math.min(1000, Math.round(((scrollY + innerHeight) / full) * 1000)));
  };
  addEventListener('scroll', onScroll, { passive: true });
  // Where the LCP time goes (server, wait before the image starts, download, render) and which element it is
  function lcpParts() {
    try {
      const e = lcpEntry;
      const nav = performance.getEntriesByType('navigation')[0];
      if (!e || !nav) return;
      const ttfb = Math.max(0, nav.responseStart - (nav.activationStart || 0));
      let start = ttfb;
      let end = ttfb;
      if (e.url) {
        const r = performance.getEntriesByName(e.url, 'resource')[0];
        if (r) { start = Math.max(ttfb, (r.requestStart || r.startTime) - (nav.activationStart || 0)); end = Math.max(start, r.responseEnd - (nav.activationStart || 0)); }
      }
      const lcp = Math.max(end, e.startTime - (nav.activationStart || 0));
      [['LCP_TTFB', ttfb], ['LCP_DELAY', start - ttfb], ['LCP_LOAD', end - start], ['LCP_RENDER', lcp - end]].forEach(([x, v]) => add('vital', { x, v: Math.round(v) }));
      const el = e.element;
      const box = el && el.closest('[class]');
      const cls = box ? String(box.className).trim().split(/\s+/).filter((c) => !/^(is-|js|img-fade|page-width)/.test(c))[0] || '' : '';
      add('lcp_el', { x: `${el ? el.tagName.toLowerCase() : 'removed'}${cls ? ` .${cls}` : ''}${e.url ? '' : ' (text)'}`.slice(0, 120), v: Math.round(lcp) });
    } catch { /* attribution is best effort */ }
  }
  let done = false;
  function finalize() {
    if (done) return;
    done = true;
    for (const m of ['LCP', 'INP', 'CLS']) if (vitals[m] != null) add('vital', { x: m, v: m === 'CLS' ? Math.round(vitals[m] * 1000) / 1000 : Math.round(vitals[m]) });
    lcpParts();
    onScroll();
    add('scroll', { v: depth });
    if (heatOn && consent()) add('hs', { v: reach });
  }
});
